import { PickedFile } from '@capawesome/capacitor-file-picker';
import { Base64Image, ProjectDoc } from 'common';
import { blobToBase64, resizeImage } from '../../global/utils/data';

/**
 * Utility functions for project data formatting, parsing, and manipulation
 */
export class ProjectUtils {
  /**
   * Format address line 2 from project document
   */
  static getAddressLine2(project: ProjectDoc): string {
    return `${project.city}, ${project.state_code} ${project.zip}`;
  }

  static getThumbnailDataUrl(logo?: Base64Image | null): string | null {
    if (!logo?.data) return null;
    if (logo.data.startsWith('data:')) return logo.data;
    return `data:${logo.mimeType || 'image/jpeg'};base64,${logo.data}`;
  }

  /**
   * Get thumbnail URL from project document
   */
  static getThumbnailUrl(project: ProjectDoc): string {
    return this.getThumbnailDataUrl(project.thumbnail) || '/assets/img/card-media.png';
  }

  static logoFromDataUrl(dataUrl: string, width = 0, height = 0): Base64Image {
    const comma = dataUrl.indexOf(',');
    const meta = comma >= 0 ? dataUrl.substring(0, comma) : '';
    const mimeMatch = meta.match(/data:([^;]+)/);
    return {
      data: comma >= 0 ? dataUrl.substring(comma + 1) : dataUrl,
      mimeType: mimeMatch?.[1] || 'image/jpeg',
      width,
      height,
    };
  }

  static setLogo(project: ProjectDoc, logo: Base64Image): ProjectDoc {
    return { ...project, thumbnail:logo };
  }

  /**
   * Set thumbnail data on project document from a data URL string
   */
  static setThumbnailFromDataUrl(project: ProjectDoc, dataUrl: string): ProjectDoc {
    return this.setLogo(project, this.logoFromDataUrl(dataUrl));
  }

  /**
   * Process and set thumbnail from a picked file
   * Resizes image to 400x225 and converts to WebP format
   */
  static async setThumbnailFromFile(project: ProjectDoc, file: PickedFile): Promise<ProjectDoc> {
    if (file) {
      const blob = await resizeImage(file.blob, 400, 225, 'image/webp');
      const base64 = await blobToBase64(blob);
      return this.setLogo(project, { data: base64, mimeType: blob.type, width: 400, height: 225 });
    }
    return project;
  }

  /**
   * Check if project has a thumbnail
   */
  static hasThumbnail(project: ProjectDoc): boolean {
    return !!project.thumbnail?.data;
  }

  /**
   * Clear thumbnail from project document
   */
  static clearThumbnail(project: ProjectDoc): ProjectDoc {
    const next = { ...project };
    delete next.thumbnail;
    return next;
  }
}
